import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

// Palette Corporate Premium (Dark Gray & Beige)
const colors = {
  bgDark: '#121214',
  bgMedium: '#1C1C1F',
  bgLight: '#252529',
  beigeLight: '#F5EFEB',
  beigeMain: '#D4C5B0',
  beigeDark: '#A3947F',
  textGray: '#A0A0A5',
  border: 'rgba(212, 197, 176, 0.15)',
};

// Données pour les piliers RSE (Couleurs spécifiques supprimées pour unifier le thème)
const rsePillars = [
  {
    title: 'Environnemental',
    description: 'Gestion des impacts environnementaux et transition écologique.',
    icon: '🌱',
    features: ['Réduction d\'empreinte carbone', 'Gestion des déchets', 'Économie circulaire', 'Énergies renouvelables']
  },
  {
    title: 'Social',
    description: 'Développement humain et conditions de travail.',
    icon: '👥',
    features: ['Diversité et inclusion', 'Formation et développement', 'Santé et sécurité', 'Dialogue social']
  },
  {
    title: 'Gouvernance',
    description: 'Éthique des affaires, conformité et transparence.',
    icon: '🏛️',
    features: ['Conformité réglementaire', 'Lutte anti-corruption', 'Transparence financière', 'Gouvernance participative']
  },
  {
    title: 'Économique',
    description: 'Performance durable et création de valeur partagée.',
    icon: '📈',
    features: ['Investissement responsable', 'Achats éthiques', 'Innovation durable', 'Résilience économique']
  }
];

// Données pour les services
const services = [
  {
    title: 'Audit & Diagnostic',
    description: 'Évaluation rigoureuse de la maturité RSE de votre structure face aux standards du marché.',
    icon: '📊',
    steps: ['Analyse approfondie des pratiques', 'Benchmark sectoriel', 'Identification des écarts (Gap analysis)', 'Recommandations stratégiques']
  },
  {
    title: 'Stratégie RSE',
    description: 'Élaboration d\'une feuille de route sur mesure pour intégrer la RSE à votre business model.',
    icon: '🎯',
    steps: ['Définition d\'objectifs SMART', 'Structuration du plan d\'action', 'Mise en place des KPI', 'Calendrier de déploiement']
  },
  {
    title: 'Reporting ESG',
    description: 'Conception et rédaction de rapports de durabilité conformes aux exigences réglementaires.',
    icon: '📋',
    steps: ['Collecte sécurisée des données', 'Rédaction éditoriale du rapport', 'Vérification de conformité', 'Stratégie de publication']
  },
  {
    title: 'Formation & Sensibilisation',
    description: 'Accompagnement du changement et formation de vos équipes dirigeantes et opérationnelles.',
    icon: '🎓',
    steps: ['Programmes sur mesure', 'Ateliers de codéveloppement', 'Suivi de l\'acquisition des compétences', 'Évaluation d\'impact']
  }
];

const ResponsabilitesEntr = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [activeService, setActiveService] = useState(0);
  const [showStats, setShowStats] = useState(false);
  
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' });

  // Calcul des styles responsives
  const computedStyles = useMemo(() => {
    return {
      section: {
        position: 'relative',
        background: `linear-gradient(180deg, ${colors.bgDark} 0%, #17171A 50%, ${colors.bgDark} 100%)`,
        color: colors.beigeLight,
        minHeight: '100vh',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
      
      animatedBackground: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 1, overflow: 'hidden',
      },
      
      noiseOverlay: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.04'/%3E%3C/svg%3E")`,
        pointerEvents: 'none', zIndex: 1,
      },
      
      gradientSphere: {
        position: 'absolute', top: '10%', right: '-10%',
        width: '50vw', height: '50vw',
        background: `radial-gradient(circle, rgba(212, 197, 176, 0.03) 0%, transparent 60%)`,
        filter: 'blur(80px)',
      },
      
      // Hero Section
      hero: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '6rem 1.5rem' : isTablet ? '8rem 3rem' : '10rem 5%',
      },
      
      heroContent: {
        maxWidth: '1440px', margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
        gap: isMobile ? '3rem' : '5rem',
        alignItems: 'center',
      },
      
      titleLine: {
        height: '1px',
        background: `linear-gradient(90deg, ${colors.beigeMain}, transparent)`,
        marginBottom: isMobile ? '1.5rem' : '2rem',
        width: isMobile ? '60px' : '100px',
      },
      
      mainTitle: {
        fontSize: isMobile ? '2.5rem' : isTablet ? '3.2rem' : '4.2rem',
        lineHeight: 1.1,
        fontWeight: 300,
        marginBottom: isMobile ? '1rem' : '1.5rem',
        color: colors.beigeLight,
        letterSpacing: '-1px',
      },
      
      titleHighlight: {
        color: colors.beigeMain,
        fontStyle: 'italic',
      },
      
      subtitle: {
        fontSize: isMobile ? '1.1rem' : isTablet ? '1.3rem' : '1.6rem',
        color: colors.textGray,
        fontWeight: 300,
        display: 'block',
        marginTop: isMobile ? '0.5rem' : '1rem',
      },
      
      heroTextWrapper: {
        display: 'flex', flexDirection: 'column', gap: '1.5rem',
        marginBottom: '3rem', marginTop: '2rem',
      },
      
      heroText: {
        fontSize: '1.05rem', lineHeight: 1.7, color: colors.textGray, margin: 0, fontWeight: 300,
        paddingLeft: '1.5rem', borderLeft: `1px solid ${colors.border}`,
      },
      
      statsContainer: {
        display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center',
        padding: '2rem 0', borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`,
      },
      
      statCard: {
        textAlign: isMobile ? 'center' : 'left', flex: 1, padding: isMobile ? '1rem 0' : '0 2rem',
      },
      
      statNumber: {
        fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 300, color: colors.beigeMain,
        fontFamily: "'Playfair Display', serif", marginBottom: '0.2rem',
      },
      
      statLabel: {
        fontSize: '0.8rem', color: colors.textGray, textTransform: 'uppercase', letterSpacing: '1px',
      },
      
      statDivider: {
        width: isMobile ? '50%' : '1px', height: isMobile ? '1px' : '50px', background: colors.border,
      },
      
      heroImageContainer: {
        position: 'relative', padding: '20px',
      },
      
      imageBackground: {
        position: 'absolute', top: '20px', left: '20px', right: '-20px', bottom: '-20px',
        border: `1px solid ${colors.beigeDark}`, borderRadius: '2px', zIndex: -1,
      },
      
      imageWrapper: {
        position: 'relative', height: isMobile ? '400px' : isTablet ? '500px' : '650px',
        borderRadius: '2px', overflow: 'hidden',
      },
      
      image: {
        width: '100%', height: '100%', objectFit: 'cover',
        filter: 'grayscale(30%) contrast(1.1) brightness(0.85)',
      },
      
      imageOverlay: {
        position: 'absolute', inset: 0,
        background: `linear-gradient(to top, rgba(18,18,20,0.9) 0%, transparent 50%)`,
      },
      
      imageBadge: {
        position: 'absolute', bottom: '0', left: '0',
        background: colors.bgMedium, padding: '1.5rem 2rem',
        borderTop: `1px solid ${colors.border}`, borderRight: `1px solid ${colors.border}`,
        display: 'flex', alignItems: 'center', gap: '1.2rem', zIndex: 2,
      },
      
      badgeIcon: {
        fontSize: '1.8rem',
        filter: 'grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9)', // Effet Bronze/Or
      },
      
      badgeText: {
        display: 'flex', flexDirection: 'column', gap: '0.2rem',
      },
      
      badgeTitle: {
        color: colors.beigeMain, fontSize: '1rem', fontWeight: 400, fontFamily: "'Playfair Display', serif",
      },
      
      badgeSubtitle: {
        color: colors.textGray, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px',
      },
      
      // RSE Pillars Section
      pillarsSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgLight, borderTop: `1px solid ${colors.border}`,
      },
      
      sectionHeader: {
        textAlign: 'center', marginBottom: isMobile ? '3rem' : '5rem',
      },
      
      sectionTitle: {
        fontSize: isMobile ? '2rem' : '3rem', fontWeight: 300, color: colors.beigeLight,
        marginBottom: '1rem', letterSpacing: '-0.5px',
      },
      
      sectionSubtitle: {
        fontSize: '1.1rem', color: colors.textGray, maxWidth: '600px', margin: '0 auto', fontWeight: 300,
      },
      
      pillarsGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '1.5rem', maxWidth: '1440px', margin: '0 auto',
      },
      
      pillarCard: {
        background: colors.bgMedium, borderRadius: '2px', padding: '2.5rem',
        border: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', position: 'relative',
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      },
      
      pillarIcon: {
        fontSize: '2rem', marginBottom: '1.5rem',
        filter: 'grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9)',
      },
      
      pillarTitle: {
        fontSize: '1.2rem', fontWeight: 400, color: colors.beigeLight, marginBottom: '1rem',
      },
      
      pillarDescription: {
        fontSize: '0.95rem', color: colors.textGray, lineHeight: 1.6, flex: 1, fontWeight: 300, marginBottom: '2rem',
      },
      
      pillarFeatures: {
        listStyle: 'none', padding: 0, margin: 0, borderTop: `1px solid ${colors.border}`, paddingTop: '1.5rem',
      },
      
      pillarFeature: {
        fontSize: '0.85rem', color: colors.textGray, marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
      },
      
      featureDot: {
        width: '4px', height: '4px', backgroundColor: colors.beigeDark, borderRadius: '50%', flexShrink: 0,
      },
      
      // Services Tabs Section
      servicesSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgDark,
      },
      
      tabsContainer: {
        maxWidth: '1200px', margin: '0 auto',
      },
      
      tabsHeader: {
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        borderBottom: `1px solid ${colors.border}`, marginBottom: '3rem',
      },
      
      tabButton: {
        flex: 1, padding: '1.2rem 1rem', background: 'transparent', border: 'none',
        color: colors.textGray, fontSize: '1.1rem', fontWeight: 400,
        cursor: 'pointer', transition: 'all 0.3s ease', borderBottom: '2px solid transparent',
        marginBottom: '-1px',
      },
      
      activeTab: {
        color: colors.beigeMain, borderBottom: `2px solid ${colors.beigeMain}`,
      },
      
      tabContent: {
        background: colors.bgMedium, border: `1px solid ${colors.border}`, borderRadius: '2px',
        padding: isMobile ? '2rem 1.5rem' : '4rem', display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.5fr 1fr', gap: '4rem', alignItems: 'center',
      },
      
      tabTitle: {
        fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight: 300, color: colors.beigeLight,
        marginBottom: '1.5rem', fontFamily: "'Playfair Display', serif",
      },
      
      tabDescription: {
        fontSize: '1.05rem', color: colors.textGray, lineHeight: 1.8, marginBottom: '2.5rem', fontWeight: 300,
      },
      
      tabSteps: {
        listStyle: 'none', padding: 0, margin: '0 0 2rem 0',
      },
      
      tabStep: {
        color: colors.beigeLight, marginBottom: '1.2rem', display: 'flex', alignItems: 'flex-start', gap: '1rem',
        fontSize: '1rem', fontWeight: 300,
      },
      
      stepNumber: {
        color: colors.beigeMain, fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', fontStyle: 'italic',
      },
      
      visualCard: {
        background: colors.bgLight, border: `1px solid ${colors.border}`, padding: '2.5rem',
        display: 'flex', flexDirection: 'column', gap: '1.5rem', textAlign: 'center', alignItems: 'center',
      },
      
      visualTitle: {
        fontSize: '1.1rem', fontWeight: 400, color: colors.beigeMain, textTransform: 'uppercase', letterSpacing: '1px',
      },
    };
  }, [isMobile, isTablet]);

  // Animations Premium
  const premiumTransition = { duration: 1, ease: [0.16, 1, 0.3, 1] };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const fadeUpVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: premiumTransition }
  };

  const tabVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
    exit: { opacity: 0, y: -15, transition: { duration: 0.3 } }
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Responsabilité Sociale des Entreprises (RSE) | KMJ & Associés</title>
        <meta name="description" content="Accompagnement RSE : stratégie durable, conformité réglementaire, reporting ESG, formation. Transformez vos engagements en valeur ajoutée." />
      </Helmet>

      <motion.section style={computedStyles.section} initial="hidden" animate="visible" variants={containerVariants}>
        
        {/* Background Overlay */}
        <div style={computedStyles.animatedBackground}>
          <div style={computedStyles.gradientSphere} />
          <div style={computedStyles.noiseOverlay} />
        </div>

        {/* Hero Section */}
        <motion.div style={computedStyles.hero} variants={containerVariants}>
          <div style={computedStyles.heroContent}>
            <motion.div variants={fadeUpVariants}>
              <div style={computedStyles.titleLine} />
              <h1 style={computedStyles.mainTitle}>
                Responsabilité sociale
                <br />
                <span style={computedStyles.titleHighlight}>des entreprises.</span>
              </h1>
              <span style={computedStyles.subtitle}>
                Transformer l'engagement en valeur durable.
              </span>

              <motion.div style={computedStyles.heroTextWrapper}>
                <p style={computedStyles.heroText}>
                  Le cabinet KMJ & Associés accompagne les directions générales dans la structuration et le déploiement de leur politique RSE (ESG), en parfaite adéquation avec leurs impératifs de croissance.
                </p>
                <p style={computedStyles.heroText}>
                  Nous sécurisons votre conformité face aux normes nationales et européennes (CSRD, Devoir de vigilance) tout en concevant des chartes éthiques et des mécanismes de contrôle interne de haut niveau.
                </p>
              </motion.div>

              {/* Stats Bar */}
              <motion.div style={computedStyles.statsContainer}>
                <div style={computedStyles.statCard}>
                  <motion.div style={computedStyles.statNumber} initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1 }}>
                    200+
                  </motion.div>
                  <div style={computedStyles.statLabel}>Audits réalisés</div>
                </div>
                <div style={computedStyles.statDivider} />
                <div style={computedStyles.statCard}>
                  <motion.div style={computedStyles.statNumber} initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1, delay: 0.2 }}>
                    ISO
                  </motion.div>
                  <div style={computedStyles.statLabel}>Standards maîtrisés</div>
                </div>
                <div style={computedStyles.statDivider} />
                <div style={computedStyles.statCard}>
                  <motion.div style={computedStyles.statNumber} initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1, delay: 0.4 }}>
                    98%
                  </motion.div>
                  <div style={computedStyles.statLabel}>Conformité ESG</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image & Offset Frame */}
            <motion.div style={computedStyles.heroImageContainer} variants={fadeUpVariants}>
              <div style={computedStyles.imageBackground} />
              <div style={computedStyles.imageWrapper}>
                <img src="/images/L11.webp" alt="RSE" style={computedStyles.image} loading="lazy" />
                <div style={computedStyles.imageOverlay} />
                <div style={computedStyles.imageBadge}>
                  <div style={computedStyles.badgeIcon}>🌍</div>
                  <div style={computedStyles.badgeText}>
                    <div style={computedStyles.badgeTitle}>Pôle RSE & Éthique</div>
                    <div style={computedStyles.badgeSubtitle}>Développement durable</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Pillars Section */}
        <div style={computedStyles.pillarsSection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Les 4 Piliers <span style={computedStyles.titleHighlight}>ESG</span>
            </motion.h2>
            <motion.p style={computedStyles.sectionSubtitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Une approche holistique pour ancrer la responsabilité au cœur de votre gouvernance.
            </motion.p>
          </div>

          <div style={computedStyles.pillarsGrid}>
            {rsePillars.map((pillar, index) => (
              <motion.div
                key={index} style={computedStyles.pillarCard}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark }}
              >
                <div style={computedStyles.pillarIcon}>{pillar.icon}</div>
                <h3 style={computedStyles.pillarTitle}>{pillar.title}</h3>
                <p style={computedStyles.pillarDescription}>{pillar.description}</p>
                <ul style={computedStyles.pillarFeatures}>
                  {pillar.features.map((feature, i) => (
                    <li key={i} style={computedStyles.pillarFeature}>
                      <div style={computedStyles.featureDot} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Services Tabs Section */}
        <div style={computedStyles.servicesSection}>
          <div style={computedStyles.tabsContainer}>
            <div style={computedStyles.sectionHeader}>
              <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
                Notre <span style={computedStyles.titleHighlight}>Accompagnement</span>
              </motion.h2>
            </div>

            <div style={computedStyles.tabsHeader}>
              {services.map((service, index) => (
                <button
                  key={index}
                  style={{ ...computedStyles.tabButton, ...(activeService === index ? computedStyles.activeTab : {}) }}
                  onClick={() => setActiveService(index)}
                >
                  {service.title}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div 
                key={activeService} style={computedStyles.tabContent}
                variants={tabVariants} initial="hidden" animate="visible" exit="exit"
              >
                <div>
                  <h3 style={computedStyles.tabTitle}>{services[activeService].title}</h3>
                  <p style={computedStyles.tabDescription}>{services[activeService].description}</p>
                  <ul style={computedStyles.tabSteps}>
                    {services[activeService].steps.map((step, i) => (
                      <li key={i} style={computedStyles.tabStep}>
                        <div style={computedStyles.stepNumber}>0{i + 1}.</div>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Visual Card next to tab content */}
                <div style={computedStyles.visualCard}>
                  <div style={computedStyles.pillarIcon} >
                    {services[activeService].icon}
                  </div>
                  <div>
                    <h4 style={computedStyles.visualTitle}>Méthodologie</h4>
                    <p style={{ color: colors.textGray, fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
                      Une approche sur-mesure respectant les standards internationaux (GRI, SASB) et adaptée à votre secteur d'activité.
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

      </motion.section>
    </>
  );
};

export default ResponsabilitesEntr;